/**
 * ReviewAskScreen - full-screen "Liking Worthly so far?" page.
 *
 * Shown by App.js when the review trigger picks the custom ask (and from the
 * admin preview button in Settings). "Rate on the App Store" opens the App
 * Store review page; "Not now" marks it declined. Theme aware (pink/warm/blue).
 */
import { createPortal } from "react-dom";
import { usePlan } from "../../utils/plan"; // dark_03

const SERIF = "'DM Serif Display', serif";
const SANS = "'DM Sans', sans-serif";
const INK = '#1F2937';
const MUTED = '#6B7280';

const PALETTES = {
  pink: {
    wash: '#F1E8FA', accent: '#7A5FB0', btnBg: '#E4D8F4', btnFg: '#6A4FA3', shadow: '122,95,176',
    card: 'linear-gradient(160deg, #FAE3EC, #E9E1F5)', heart: '\u{1F49C}',
    rows: ['#FAE3EC', '#E9E1F5', '#E7F1DB'],
  },

  warm: {
    wash: '#EDF2E4', accent: '#2D5A3D', btnBg: '#DCE7D6', btnFg: '#2D5A3D', shadow: '45,90,61',
    card: 'linear-gradient(160deg, #F7E7D9, #E9EFD9)', heart: '\u{1F49A}',
    rows: ['#E9EFD9', '#F7E7D9', '#F0E3D9'],
  },

  blue: {
    wash: '#E5EAFB', accent: '#3A4DA8', btnBg: '#D6DEF7', btnFg: '#3A4DA8', shadow: '58,77,168',
    card: 'linear-gradient(160deg, #E1E8FA, #E4E1F5)', heart: '\u{1F499}',
    rows: ['#E1E8FA', '#E4E1F5', '#DCEFF0'],
  }
};

const ROWS = [
  { emoji: '\u{23F1}\u{FE0F}', text: 'It takes less than a minute to leave a rating!' },
  { emoji: '\u{1F50D}', text: 'It helps Worthly show up in App Store searches' },
  { emoji: '\u{1F48C}', text: "I'm grateful for every review, and read all your feedback!" },
];

// dark_03: Worthly 2.0 look - white cards on the lavender page, DM Sans and a
// black button. Dark mode flips it like every other 2.0 screen.
const W2C = { page: '#F4F1FB', card: '#FFFFFF', ink: '#26252E', soft: '#8B879A', line: '#EFECF5', lil: '#ECE6FA', lilT: '#7A5DD3', mint: '#DFF2E7', blue: '#DFE9F9', btn: '#1F1E24' };
const W2_BUBBLES = [W2C.lil, W2C.mint, W2C.blue];

const ReviewAskW2 = ({ onRate, onNotNow }) => createPortal(
  <div
    className="fixed inset-0 animate-in fade-in duration-300"
    style={{ zIndex: 9999, background: W2C.page, overflowY: 'auto', WebkitOverflowScrolling: 'touch', fontFamily: SANS }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="review-ask-title"
  >
    <div className="mx-auto flex flex-col" style={{ minHeight: '100%', maxWidth: 440, padding: 'calc(env(safe-area-inset-top, 0px) + 28px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
      <div style={{ background: W2C.card, borderRadius: 30, padding: '26px 22px 22px', textAlign: 'center', marginTop: 'auto' }}>
        <img src="/default-icon.png" alt="Worthly" style={{ width: 78, height: 78, borderRadius: 22, display: 'block', margin: '0 auto', boxShadow: '0 10px 22px rgba(90,70,160,0.18)' }} />
        <div style={{ fontSize: 19, letterSpacing: 4, color: '#F5C451', lineHeight: 1, marginTop: 14 }}>{'★★★★★'}</div>
        <h2 id="review-ask-title" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.12, color: W2C.ink, margin: '12px 0 0' }}>Liking Worthly so far?</h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.5, color: W2C.soft, margin: '8px 0 0' }}>Worthly was made and is mostly run by a one-woman team, so every review helps!</p>
      </div>
      <div style={{ background: W2C.card, borderRadius: 28, padding: '6px 18px', marginTop: 12 }}>
        {ROWS.map((r, i) => (
          <div key={r.text} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 0', borderTop: i ? `1px solid ${W2C.line}` : 0, fontSize: 14.5, fontWeight: 500, lineHeight: 1.4, color: W2C.ink }}>
            <span style={{ width: 42, height: 42, borderRadius: 15, background: W2_BUBBLES[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }}>{r.emoji}</span>
            {r.text}
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', marginTop: 18, fontSize: 14, color: W2C.soft, lineHeight: 1.5 }}>
        Thank you for being here! {'\u{1F49C}'}
        <span style={{ display: 'block', color: W2C.lilT, fontWeight: 600, fontSize: 16, marginTop: 2 }}>Layemi</span>
        Founder of Worthly
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 24 }}>
        <button type="button" onClick={onRate} style={{ display: 'block', width: '100%', border: 0, borderRadius: 999, padding: 16, background: W2C.btn, color: '#FFFFFF', fontFamily: SANS, fontSize: 15.5, fontWeight: 600 }}>
          Rate on the App Store
        </button>
        <button type="button" onClick={onNotNow} style={{ display: 'block', width: '100%', minHeight: 44, border: 0, background: 'transparent', color: W2C.soft, fontFamily: SANS, fontSize: 14.5, fontWeight: 600, padding: '14px 0 2px' }}>
          Not now
        </button>
      </div>
    </div>
  </div>,
  document.body
);

const ReviewAskScreen = ({ theme = 'pink', onRate, onNotNow }) => {
  const plan = usePlan(); // dark_03
  const P = PALETTES[theme] || PALETTES.pink;
  if (plan) return <ReviewAskW2 onRate={onRate} onNotNow={onNotNow} />;

  return createPortal(
    <div
      className="fixed inset-0 animate-in fade-in duration-300"
      style={{ zIndex: 9999, background: `linear-gradient(180deg, ${P.wash} 0%, #FFFFFF 52%)`, overflowY: 'auto', WebkitOverflowScrolling: 'touch', fontFamily: SANS }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-ask-title"
    >
      <div
        className="mx-auto flex flex-col text-center"
        style={{
          minHeight: '100%', maxWidth: 440,
          padding: 'calc(env(safe-area-inset-top, 0px) + 24px) 24px calc(env(safe-area-inset-bottom, 0px) + 22px)',
        }}
      >
        {/* Postcard */}
        <div style={{ position: 'relative', height: 196, margin: 'auto 0 14px', flexShrink: 0 }}>
          <div
            style={{
              position: 'absolute', left: '50%', top: 16, width: 214, height: 150, marginLeft: -107,
              borderRadius: 22, background: P.card, transform: 'rotate(-5deg)',
              boxShadow: `0 18px 36px rgba(${P.shadow},0.22), inset 0 1px 0 rgba(255,255,255,0.8)`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
          >
            <img
              src="/default-icon.png"
              alt="Worthly"
              style={{ width: 76, height: 76, borderRadius: 20, display: 'block', boxShadow: `0 0 0 4px #FFFFFF, 0 8px 18px rgba(${P.shadow},0.22)` }}
            />
            <div style={{ fontSize: 18, letterSpacing: 4, color: '#F2B53A', lineHeight: 1 }}>{'★★★★★'}</div>
          </div>
        </div>

        <h2 id="review-ask-title" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.12, color: INK, margin: '0 0 10px' }}>
          Liking Worthly so far?
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.55, color: INK, margin: '0 6px 20px' }}>
          Worthly was made and is mostly run by a one-woman team, so every review helps!
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left' }}>
          {ROWS.map((r, i) => (
            <div key={r.text} style={{ display: 'flex', alignItems: 'center', gap: 12, borderRadius: 14, padding: '12px 14px', background: P.rows[i], fontSize: 15, fontWeight: 500, color: INK }}>
              <span style={{ fontSize: 20, width: 26, textAlign: 'center', flexShrink: 0 }}>{r.emoji}</span>
              {r.text}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 22 }}>
          <p style={{ fontSize: 14.5, lineHeight: 1.4, color: INK, margin: 0 }}>Thank you for being here!</p>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 19, color: P.accent, margin: '2px 0 0' }}>Layemi</p>
          <p style={{ fontSize: 12.5, color: MUTED, margin: '1px 0 0' }}>Founder of Worthly</p>
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 24 }}>
          <button
            type="button"
            onClick={onRate}
            style={{
              display: 'block', width: '100%', border: 0, borderRadius: 999, padding: 17,
              background: P.btnBg, color: P.btnFg, fontFamily: SANS, fontSize: 17, fontWeight: 600,
              boxShadow: `inset 0 -2px 0 rgba(${P.shadow},0.10)`,
            }}
          >
            Rate on the App Store
          </button>
          <button
            type="button"
            onClick={onNotNow}
            style={{ display: 'block', width: '100%', minHeight: 44, border: 0, background: 'transparent', color: MUTED, fontFamily: SANS, fontSize: 16, fontWeight: 500, padding: '14px 0 2px' }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReviewAskScreen;
